/**
 * Solutions Page Content Configuration
 * ICP-specific content for each solution page
 */

import type { SupportedLanguage } from '@/lib/seo'

export type SolutionSlug = 
  | 'photographers'
  | 'doulas'
  | 'tattoo-artists'
  | 'wedding-planners'
  | 'personal-organizers'
  | 'event-planners'
  | 'yoga-instructors'
  | 'fitness-coaches'
  | 'hair-stylists'
  | 'nurses'
  | 'pop-up-decorators'
  | 'telemedicine-professionals'
  | 'tarot-readers'
  | 'reiki-practitioners'

export type SolutionImage = {
  src: string
  alt: string
  caption?: string
}

export type SolutionContent = {
  slug: SolutionSlug
  icon: string
  color: string
  images: {
    hero: SolutionImage
    gallery: SolutionImage[]
  }
  hero: {
    badge: string
    title: string
    subtitle: string
    cta: string
  }
  benefits: Array<{
    icon: string
    title: string
    description: string
  }>
  packages: Array<{
    name: string
    description: string
  }>
  testimonial?: {
    quote: string
    author: string
    role: string
    avatar?: string
  }
  faq: Array<{
    question: string
    answer: string
  }>
}

export const SOLUTIONS_CONTENT: Record<SolutionSlug, Record<SupportedLanguage, SolutionContent>> = {
  photographers: {
    pt: {
      slug: 'photographers',
      icon: '📸',
      color: 'purple',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704366/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_orfvvj.png', 
          caption: 'Integração com Google Calendar para bloqueio automático de datas',
          alt: 'Fotógrafo profissional em ação',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704371/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_1_royxbd.png', alt: 'Use o Pinday Mobbile para gerenciar suas reservas', caption: 'Gerencie suas reservas de qualquer lugar' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704371/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_2_qrlhp8.png', alt: 'Métricas detalhadas para acompanhar seu desempenho', caption: 'Métricas detalhadas para acompanhar seu desempenho' },
        ],
      },
      hero: {
        badge: 'Para Fotógrafos',
        title: 'Depósito que bloqueia seu sábado como fotógrafo',
        subtitle: 'Crie um link de sinal que bloqueia a data no Google Calendar assim que o cliente paga. Casamentos, ensaios, eventos — tudo com contrato e política de cancelamento.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Sinal por Pix e cartão',
          description: 'Receba o sinal antes do ensaio. O cliente escolhe pagar por Pix com QR code ou cartão de crédito.',
        },
        {
          icon: '📅',
          title: 'Bloqueio automático de data',
          description: 'Assim que o cliente paga, a data é bloqueada no seu Google Calendar. Sem trabalho manual.',
        },
        {
          icon: '📝',
          title: 'Contrato digital incluso',
          description: 'Termos de uso, políticas de reagendamento e cancelamento. O cliente aceita antes de pagar.',
        },
        {
          icon: '📦',
          title: '3 pacotes + add-ons',
          description: 'Monte pacotes como Básico, Completo e Premium. Adicione álbum, pré-wedding, fotos extras como add-ons.',
        },
      ],
      packages: [
        { name: 'Cobertura Básica', description: '4 horas, 100 fotos editadas' },
        { name: 'Cobertura Completa', description: '8 horas, 300 fotos, álbum 20x30' },
        { name: 'Cobertura Premium', description: 'Dia inteiro, fotos ilimitadas, álbum luxo + pré-wedding' },
      ],
      testimonial: {
        quote: 'Antes eu perdia sábados inteiros quando o cliente cancelava em cima da hora. Agora recebo o sinal e se cancelar, tenho a política de cancelamento clara.',
        author: 'Mariana Costa',
        role: 'Fotógrafa de Casamentos',
        avatar: 'https://randomuser.me/api/portraits/women/1.jpg',
      },
      faq: [
        {
          question: 'Como funciona o sinal para fotografia?',
          answer: 'Você cria uma proposta com pacotes, define o valor do sinal (geralmente 30-50%) e envia o link pro cliente. Ele escolhe o pacote, aceita os termos e paga. A data é bloqueada automaticamente.',
        },
        {
          question: 'Posso receber por Pix?',
          answer: 'Sim! O cliente pode pagar por Pix (via QR code) ou cartão de crédito. O Pix é processado via Asaas e o cartão via Stripe.',
        },
        {
          question: 'E se o cliente cancelar?',
          answer: 'Você define a política de cancelamento antes. Ex: até 30 dias = reembolso total, até 7 dias = 50%, menos de 7 dias = sem reembolso.',
        },
        {
          question: 'Funciona para ensaios também?',
          answer: 'Sim! Funciona para casamentos, ensaios, formaturas, aniversários, eventos corporativos — qualquer sessão que precise de reserva de data.',
        },
      ],
    },
    en: {
      slug: 'photographers',
      icon: '📸',
      color: 'purple',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704366/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_orfvvj.png',
          alt: 'Professional photographer in action',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704371/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_1_royxbd.png', alt: 'Wedding photography' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704371/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_2_qrlhp8.png', alt: 'Photo shoot' },
        ],
      },
      hero: {
        badge: 'For Photographers',
        title: 'Deposit that holds your Saturday as a photographer',
        subtitle: 'Create a deposit link that blocks the date on Google Calendar as soon as your client pays. Weddings, portraits, events — all with contract and cancellation policy.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Deposit via card',
          description: 'Receive the retainer before the shoot. Client pays by credit card with secure checkout.',
        },
        {
          icon: '📅',
          title: 'Automatic date blocking',
          description: 'As soon as the client pays, the date is blocked on your Google Calendar. No manual work.',
        },
        {
          icon: '📝',
          title: 'Digital contract included',
          description: 'Terms of use, rescheduling and cancellation policies. Client accepts before paying.',
        },
        {
          icon: '📦',
          title: '3 packages + add-ons',
          description: 'Build packages like Basic, Complete and Premium. Add album, pre-wedding, extra photos as add-ons.',
        },
      ],
      packages: [
        { name: 'Basic Coverage', description: '4 hours, 100 edited photos' },
        { name: 'Full Coverage', description: '8 hours, 300 photos, 20x30 album' },
        { name: 'Premium Coverage', description: 'Full day, unlimited photos, luxury album + engagement session' },
      ],
      testimonial: {
        quote: 'Before I used to lose entire Saturdays when clients canceled last minute. Now I receive the deposit and if they cancel, I have a clear cancellation policy.',
        author: 'Sarah Johnson',
        role: 'Wedding Photographer',
        avatar: 'https://randomuser.me/api/portraits/women/9.jpg',
      },
      faq: [
        {
          question: 'How does the photography deposit work?',
          answer: 'You create a proposal with packages, set the deposit amount (usually 30-50%) and send the link to your client. They choose the package, accept the terms and pay. The date is blocked automatically.',
        },
        {
          question: 'What payment methods are available?',
          answer: 'Clients can pay by credit card via Stripe. The checkout is secure and supports Apple Pay and Google Pay.',
        },
        {
          question: 'What if the client cancels?',
          answer: 'You define the cancellation policy upfront. Ex: up to 30 days = full refund, up to 7 days = 50%, less than 7 days = no refund.',
        },
        {
          question: 'Does it work for portrait sessions too?',
          answer: 'Yes! It works for weddings, portraits, graduations, birthdays, corporate events — any session that needs a date reservation.',
        },
      ],
    },
    es: {
      slug: 'photographers',
      icon: '📸',
      color: 'purple',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704366/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_orfvvj.png',
          alt: 'Fotógrafo profesional en acción',
        },
        gallery: [
         { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704371/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_1_royxbd.png', alt: 'Fotografia de casamento' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767704371/Gemini_Generated_Image_c7jnfdc7jnfdc7jn_2_qrlhp8.png', alt: 'Ensaio fotográfico' },
        ],
      },
      hero: {
        badge: 'Para Fotógrafos',
        title: 'Seña que reserva tu sábado como fotógrafo',
        subtitle: 'Crea un link de seña que bloquea la fecha en Google Calendar en cuanto el cliente paga. Bodas, sesiones, eventos — todo con contrato y política de cancelación.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Seña por tarjeta',
          description: 'Recibe la seña antes de la sesión. El cliente paga con tarjeta de crédito con checkout seguro.',
        },
        {
          icon: '📅',
          title: 'Bloqueo automático de fecha',
          description: 'En cuanto el cliente paga, la fecha se bloquea en tu Google Calendar. Sin trabajo manual.',
        },
        {
          icon: '📝',
          title: 'Contrato digital incluido',
          description: 'Términos de uso, políticas de reagendamiento y cancelación. El cliente acepta antes de pagar.',
        },
        {
          icon: '📦',
          title: '3 paquetes + add-ons',
          description: 'Arma paquetes como Básico, Completo y Premium. Agrega álbum, pre-boda, fotos extras como add-ons.',
        },
      ],
      packages: [
        { name: 'Cobertura Básica', description: '4 horas, 100 fotos editadas' },
        { name: 'Cobertura Completa', description: '8 horas, 300 fotos, álbum 20x30' },
        { name: 'Cobertura Premium', description: 'Día completo, fotos ilimitadas, álbum de lujo + pre-boda' },
      ],
      testimonial: {
        quote: 'Antes perdía sábados enteros cuando el cliente cancelaba a último momento. Ahora recibo la seña y si cancela, tengo la política de cancelación clara.',
        author: 'María García',
        role: 'Fotógrafa de Bodas',
        avatar: 'https://randomuser.me/api/portraits/women/19.jpg',
      },
      faq: [
        {
          question: '¿Cómo funciona la seña para fotografía?',
          answer: 'Creas una propuesta con paquetes, defines el valor de la seña (generalmente 30-50%) y envías el link al cliente. Él elige el paquete, acepta los términos y paga. La fecha se bloquea automáticamente.',
        },
        {
          question: '¿Qué métodos de pago están disponibles?',
          answer: 'Los clientes pueden pagar con tarjeta de crédito vía Stripe. El checkout es seguro y soporta Apple Pay y Google Pay.',
        },
        {
          question: '¿Y si el cliente cancela?',
          answer: 'Tú defines la política de cancelación antes. Ej: hasta 30 días = reembolso total, hasta 7 días = 50%, menos de 7 días = sin reembolso.',
        },
        {
          question: '¿Funciona para sesiones también?',
          answer: '¡Sí! Funciona para bodas, sesiones, graduaciones, cumpleaños, eventos corporativos — cualquier sesión que necesite reserva de fecha.',
        },
      ],
    },
  },

  doulas: {
    pt: {
      slug: 'doulas',
      icon: '🤰',
      color: 'pink',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767705393/Gemini_Generated_Image_v4bm7qv4bm7qv4bm_il6p3y.png',
          alt: 'Doula acompanhando gestante',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767705385/Gemini_Generated_Image_v4bm7qv4bm7qv4bm_1_lvm1we.png', alt: 'Acompanhamento pré-natal' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767705381/Gemini_Generated_Image_v4bm7qv4bm7qv4bm_2_woors7.png', alt: 'Apoio durante o parto' },
        ],
      },
      hero: {
        badge: 'Para Doulas',
        title: 'Reserve partos com sinal automático',
        subtitle: 'Crie um link para reservar a data prevista do parto. Pacotes de pré-natal, parto e pós-parto com contratos e políticas claras.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Sinal que garante a reserva',
          description: 'A gestante paga o sinal e você reserva a data prevista. Sem incertezas sobre a sua agenda.',
        },
        {
          icon: '📅',
          title: 'Bloqueio da DPP no calendário',
          description: 'A data prevista do parto fica bloqueada no Google Calendar assim que o pagamento é confirmado.',
        },
        {
          icon: '📝',
          title: 'Termos de acompanhamento',
          description: 'Contrato digital com escopo do acompanhamento, limites e políticas. Tudo aceito antes do pagamento.',
        },
        {
          icon: '📦',
          title: 'Pacotes flexíveis',
          description: 'Monte pacotes de pré-natal, parto e pós-parto. Adicione consultas extras como add-ons.',
        },
      ],
      packages: [
        { name: 'Pré-Natal', description: '4 encontros pré-parto + suporte WhatsApp' },
        { name: 'Parto', description: 'Acompanhamento no parto + 1 visita pós-parto' },
        { name: 'Completo', description: 'Pré-natal + parto + 3 visitas pós-parto' },
      ],
      testimonial: {
        quote: 'Minhas gestantes adoram a praticidade. Elas escolhem o pacote, aceitam os termos e pagam tudo pelo link. Eu só confirmo a data.',
        author: 'Ana Beatriz',
        role: 'Doula de Parto',
        avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
      },
      faq: [
        {
          question: 'Como funciona a reserva para doulas?',
          answer: 'Você cria uma proposta com seus pacotes (pré-natal, parto, pós-parto), define o sinal e envia o link. A gestante escolhe, aceita os termos e paga. A DPP é bloqueada no seu calendário.',
        },
        {
          question: 'E se o parto acontecer antes ou depois da DPP?',
          answer: 'Você pode configurar uma janela de disponibilidade no calendário. A proposta pode incluir termos sobre variação de datas.',
        },
        {
          question: 'Posso oferecer parcelamento?',
          answer: 'Sim! No plano Pro você pode oferecer parcelamento no cartão. Ideal para pacotes mais completos.',
        },
        {
          question: 'Como funciona o contrato?',
          answer: 'Você configura os termos uma vez e eles são apresentados para cada gestante antes do pagamento. Aceite registrado com IP e timestamp.',
        },
      ],
    },
    en: {
      slug: 'doulas',
      icon: '🤰',
      color: 'pink',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Doula supporting expecting mother',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Prenatal support' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Birth assistance' },
        ],
      },
      hero: {
        badge: 'For Doulas',
        title: 'Book births with automatic deposit',
        subtitle: 'Create a link to book the expected due date. Prenatal, birth and postpartum packages with contracts and clear policies.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Deposit that secures the booking',
          description: 'The expecting mother pays the deposit and you reserve the due date. No uncertainties about your schedule.',
        },
        {
          icon: '📅',
          title: 'Due date blocked on calendar',
          description: 'The expected due date is blocked on Google Calendar as soon as payment is confirmed.',
        },
        {
          icon: '📝',
          title: 'Support terms',
          description: 'Digital contract with scope of support, limits and policies. Everything accepted before payment.',
        },
        {
          icon: '📦',
          title: 'Flexible packages',
          description: 'Build prenatal, birth and postpartum packages. Add extra consultations as add-ons.',
        },
      ],
      packages: [
        { name: 'Prenatal', description: '4 prenatal meetings + WhatsApp support' },
        { name: 'Birth', description: 'Birth support + 1 postpartum visit' },
        { name: 'Complete', description: 'Prenatal + birth + 3 postpartum visits' },
      ],
      testimonial: {
        quote: 'My clients love the convenience. They choose the package, accept the terms and pay everything through the link. I just confirm the date.',
        author: 'Emily Rose',
        role: 'Birth Doula',
        avatar: 'https://randomuser.me/api/portraits/women/10.jpg',
      },
      faq: [
        {
          question: 'How does booking work for doulas?',
          answer: 'You create a proposal with your packages (prenatal, birth, postpartum), set the deposit and send the link. The expecting mother chooses, accepts the terms and pays. The due date is blocked on your calendar.',
        },
        {
          question: 'What if the birth happens before or after the due date?',
          answer: 'You can configure an availability window on your calendar. The proposal can include terms about date variations.',
        },
        {
          question: 'Can I offer installments?',
          answer: 'Yes! On the Pro plan you can offer installments on card. Ideal for more complete packages.',
        },
        {
          question: 'How does the contract work?',
          answer: 'You configure the terms once and they are presented to each client before payment. Acceptance recorded with IP and timestamp.',
        },
      ],
    },
    es: {
      slug: 'doulas',
      icon: '🤰',
      color: 'pink',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Doula acompañando a gestante',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Apoyo prenatal' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Asistencia en el parto' },
        ],
      },
      hero: {
        badge: 'Para Doulas',
        title: 'Reserva partos con seña automática',
        subtitle: 'Crea un link para reservar la fecha probable de parto. Paquetes de prenatal, parto y posparto con contratos y políticas claras.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Seña que asegura la reserva',
          description: 'La gestante paga la seña y tú reservas la fecha probable. Sin incertidumbres sobre tu agenda.',
        },
        {
          icon: '📅',
          title: 'FPP bloqueada en calendario',
          description: 'La fecha probable de parto queda bloqueada en Google Calendar en cuanto se confirma el pago.',
        },
        {
          icon: '📝',
          title: 'Términos de acompañamiento',
          description: 'Contrato digital con alcance del acompañamiento, límites y políticas. Todo aceptado antes del pago.',
        },
        {
          icon: '📦',
          title: 'Paquetes flexibles',
          description: 'Arma paquetes de prenatal, parto y posparto. Agrega consultas extras como add-ons.',
        },
      ],
      packages: [
        { name: 'Prenatal', description: '4 encuentros preparto + soporte WhatsApp' },
        { name: 'Parto', description: 'Acompañamiento en el parto + 1 visita posparto' },
        { name: 'Completo', description: 'Prenatal + parto + 3 visitas posparto' },
      ],
      testimonial: {
        quote: 'Mis gestantes aman la practicidad. Eligen el paquete, aceptan los términos y pagan todo por el link. Yo solo confirmo la fecha.',
        author: 'Ana María',
        role: 'Doula de Parto',
        avatar: 'https://randomuser.me/api/portraits/women/20.jpg',
      },
      faq: [
        {
          question: '¿Cómo funciona la reserva para doulas?',
          answer: 'Creas una propuesta con tus paquetes (prenatal, parto, posparto), defines la seña y envías el link. La gestante elige, acepta los términos y paga. La FPP se bloquea en tu calendario.',
        },
        {
          question: '¿Y si el parto ocurre antes o después de la FPP?',
          answer: 'Puedes configurar una ventana de disponibilidad en tu calendario. La propuesta puede incluir términos sobre variación de fechas.',
        },
        {
          question: '¿Puedo ofrecer cuotas?',
          answer: '¡Sí! En el plan Pro puedes ofrecer cuotas con tarjeta. Ideal para paquetes más completos.',
        },
        {
          question: '¿Cómo funciona el contrato?',
          answer: 'Configuras los términos una vez y se presentan a cada gestante antes del pago. Aceptación registrada con IP y timestamp.',
        },
      ],
    },
  },

  'tattoo-artists': {
    pt: {
      slug: 'tattoo-artists',
      icon: '🎨',
      color: 'orange',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767671081/1767497590020_pnbhor.png',
          alt: 'Tatuador usando o dashboard do PinDay em tablet',
          caption: 'Dashboard completo para gerenciar seus agendamentos',
        },
        gallery: [
          {
            src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767698666/Gemini_Generated_Image_avg7j1avg7j1avg7_pglmdx.png',
            alt: 'Tatuador usando o PinDay no celular',
            caption: 'Gerencie suas reservas de qualquer lugar',
          },
          {
            src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767719729/Gemini_Generated_Image_xhhvx0xhhvx0xhhv_wr6rkb.png',
            alt: 'Tatuador trabalhando com calendário do PinDay',
            caption: 'Calendário integrado com Google Calendar',
          },
        ],
      },
      hero: {
        badge: 'Para Tatuadores',
        title: 'Sinal que garante sua sessão de tattoo',
        subtitle: 'Acabe com no-shows. Receba o sinal antes da sessão e tenha políticas de cancelamento claras. Bloqueio automático da agenda.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Sinal antes de tatuar',
          description: 'O cliente paga o sinal pra garantir a sessão. Se cancelar em cima da hora, você tem a política clara.',
        },
        {
          icon: '📅',
          title: 'Agenda protegida',
          description: 'Data bloqueada no Google Calendar assim que o cliente paga. Sem furos na agenda.',
        },
        {
          icon: '📝',
          title: 'Termos e cuidados',
          description: 'Configure termos sobre cuidados pré e pós-tattoo, restrições e política de retoque.',
        },
        {
          icon: '📦',
          title: 'Sessões e pacotes',
          description: 'Crie opções por tamanho, estilo ou projeto completo. Add-ons para retoque, cor, etc.',
        },
      ],
      packages: [
        { name: 'Sessão Pequena', description: 'Até 2 horas, tamanho máximo 10cm' },
        { name: 'Sessão Média', description: '3-5 horas, tamanho até 20cm' },
        { name: 'Projeto Grande', description: 'Múltiplas sessões, braço/costas completo' },
      ],
      testimonial: {
        quote: 'Cansei de perder sessões por no-show. Agora o cliente paga o sinal e se não aparecer, eu pelo menos cobri meu tempo.',
        author: 'Lucas Ink',
        role: 'Tatuador',
        avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
      },
      faq: [
        {
          question: 'Como funciona o sinal para tatuagem?',
          answer: 'Você cria uma proposta com as opções de sessão, define o sinal (ex: 30% ou valor fixo de R$100) e envia o link. O cliente escolhe, aceita os termos e paga.',
        },
        {
          question: 'Posso incluir termos sobre cuidados?',
          answer: 'Sim! Configure termos sobre cuidados pré-sessão (não beber, dormir bem), pós-sessão (hidratação, sol) e política de retoque.',
        },
        {
          question: 'E se o cliente cancelar?',
          answer: 'Você define a política antes. Ex: mais de 48h = reagendamento grátis, menos de 48h = perde o sinal, menos de 24h = perde sinal e paga taxa adicional.',
        },
        {
          question: 'Funciona para flash day?',
          answer: 'Sim! Você pode criar propostas para flash day com designs específicos e preços fechados.',
        },
      ],
    },
    en: {
      slug: 'tattoo-artists',
      icon: '🎨',
      color: 'orange',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767671081/1767497590020_pnbhor.png',
          alt: 'Tattoo artist using PinDay dashboard on tablet',
          caption: 'Complete dashboard to manage your appointments',
        },
        gallery: [
          {
            src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767698666/Gemini_Generated_Image_avg7j1avg7j1avg7_pglmdx.png',
            alt: 'Tattoo artist using PinDay on mobile',
            caption: 'Manage your bookings from anywhere',
          },
          {
            src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767719729/Gemini_Generated_Image_xhhvx0xhhvx0xhhv_wr6rkb.png',
            alt: 'Tattoo artist working with PinDay calendar',
            caption: 'Integrated calendar with Google Calendar',
          },
        ],
      },
      hero: {
        badge: 'For Tattoo Artists',
        title: 'Deposit that secures your tattoo session',
        subtitle: 'End no-shows. Receive the deposit before the session and have clear cancellation policies. Automatic schedule blocking.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Deposit before inking',
          description: 'Client pays the deposit to secure the session. If they cancel last minute, you have a clear policy.',
        },
        {
          icon: '📅',
          title: 'Protected schedule',
          description: 'Date blocked on Google Calendar as soon as client pays. No holes in your schedule.',
        },
        {
          icon: '📝',
          title: 'Terms and care',
          description: 'Configure terms about pre and post-tattoo care, restrictions and touch-up policy.',
        },
        {
          icon: '📦',
          title: 'Sessions and packages',
          description: 'Create options by size, style or full project. Add-ons for touch-up, color, etc.',
        },
      ],
      packages: [
        { name: 'Small Session', description: 'Up to 2 hours, max size 10cm' },
        { name: 'Medium Session', description: '3-5 hours, size up to 20cm' },
        { name: 'Large Project', description: 'Multiple sessions, full arm/back' },
      ],
      testimonial: {
        quote: 'I got tired of losing sessions to no-shows. Now the client pays the deposit and if they don\'t show up, I at least covered my time.',
        author: 'Mike Ink',
        role: 'Tattoo Artist',
        avatar: 'https://randomuser.me/api/portraits/men/4.jpg',
      },
      faq: [
        {
          question: 'How does the tattoo deposit work?',
          answer: 'You create a proposal with session options, set the deposit (e.g., 30% or fixed $50) and send the link. Client chooses, accepts terms and pays.',
        },
        {
          question: 'Can I include care terms?',
          answer: 'Yes! Configure terms about pre-session care (no drinking, good sleep), post-session (moisturizing, sun) and touch-up policy.',
        },
        {
          question: 'What if the client cancels?',
          answer: 'You define the policy upfront. E.g., more than 48h = free reschedule, less than 48h = lose deposit, less than 24h = lose deposit and pay extra fee.',
        },
        {
          question: 'Does it work for flash days?',
          answer: 'Yes! You can create proposals for flash days with specific designs and fixed prices.',
        },
      ],
    },
    es: {
      slug: 'tattoo-artists',
      icon: '🎨',
      color: 'orange',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767671081/1767497590020_pnbhor.png',
          alt: 'Tatuador usando el dashboard de PinDay en tablet',
          caption: 'Dashboard completo para gestionar tus citas',
        },
        gallery: [
          {
            src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767698666/Gemini_Generated_Image_avg7j1avg7j1avg7_pglmdx.png',
            alt: 'Tatuador usando PinDay en el celular',
            caption: 'Gestiona tus reservas desde cualquier lugar',
          },
          {
            src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767719729/Gemini_Generated_Image_xhhvx0xhhvx0xhhv_wr6rkb.png',
            alt: 'Tatuador trabajando con calendario de PinDay',
            caption: 'Calendario integrado con Google Calendar',
          },
        ],
      },
      hero: {
        badge: 'Para Tatuadores',
        title: 'Seña que asegura tu sesión de tattoo',
        subtitle: 'Termina con los no-shows. Recibe la seña antes de la sesión y ten políticas de cancelación claras. Bloqueo automático de agenda.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Seña antes de tatuar',
          description: 'El cliente paga la seña para asegurar la sesión. Si cancela a último momento, tienes la política clara.',
        },
        {
          icon: '📅',
          title: 'Agenda protegida',
          description: 'Fecha bloqueada en Google Calendar en cuanto el cliente paga. Sin huecos en tu agenda.',
        },
        {
          icon: '📝',
          title: 'Términos y cuidados',
          description: 'Configura términos sobre cuidados pre y post-tattoo, restricciones y política de retoque.',
        },
        {
          icon: '📦',
          title: 'Sesiones y paquetes',
          description: 'Crea opciones por tamaño, estilo o proyecto completo. Add-ons para retoque, color, etc.',
        },
      ],
      packages: [
        { name: 'Sesión Pequeña', description: 'Hasta 2 horas, tamaño máximo 10cm' },
        { name: 'Sesión Media', description: '3-5 horas, tamaño hasta 20cm' },
        { name: 'Proyecto Grande', description: 'Múltiples sesiones, brazo/espalda completa' },
      ],
      testimonial: {
        quote: 'Me cansé de perder sesiones por no-shows. Ahora el cliente paga la seña y si no aparece, al menos cubrí mi tiempo.',
        author: 'Carlos Ink',
        role: 'Tatuador',
        avatar: 'https://randomuser.me/api/portraits/men/8.jpg',
      },
      faq: [
        {
          question: '¿Cómo funciona la seña para tatuaje?',
          answer: 'Creas una propuesta con opciones de sesión, defines la seña (ej: 30% o valor fijo de $50) y envías el link. El cliente elige, acepta términos y paga.',
        },
        {
          question: '¿Puedo incluir términos sobre cuidados?',
          answer: '¡Sí! Configura términos sobre cuidados pre-sesión (no beber, dormir bien), post-sesión (hidratación, sol) y política de retoque.',
        },
        {
          question: '¿Y si el cliente cancela?',
          answer: 'Defines la política antes. Ej: más de 48h = reagendamiento gratis, menos de 48h = pierde seña, menos de 24h = pierde seña y paga tasa adicional.',
        },
        {
          question: '¿Funciona para flash day?',
          answer: '¡Sí! Puedes crear propuestas para flash day con diseños específicos y precios cerrados.',
        },
      ],
    },
  },

  'wedding-planners': {
    pt: {
      slug: 'wedding-planners',
      icon: '💒',
      color: 'rose',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699818/Gemini_Generated_Image_e484d8e484d8e484_mplgnd.png',
          alt: 'Wedding planner organizando casamento',
          caption: 'Integre com seu Google Calendar para gerenciar suas datas',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699814/Gemini_Generated_Image_e484d8e484d8e484_1_aymixd.png', alt: 'Usando Pinday Mobile para organização de casamentos', caption: 'Organize casamentos com facilidade usando o Pinday Mobile' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699810/Gemini_Generated_Image_e484d8e484d8e484_2_iy3eek.png', alt: 'Dashboard de métricas do Pinday para wedding planners', caption: 'Acompanhe suas reservas e pagamentos em um só lugar' },
        ],
      },
      hero: {
        badge: 'Para Wedding Planners',
        title: 'Sinal que trava a data do casamento',
        subtitle: 'Assessoria completa, parcial ou do dia com contrato profissional. Calendário sincronizado automaticamente quando a noiva paga.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Sinal que garante a data',
          description: 'A noiva paga o sinal e você bloqueia a data. Casamento marcado, sem risco de perder pra concorrência.',
        },
        {
          icon: '📅',
          title: 'Calendário de eventos',
          description: 'Cada casamento fica no seu Google Calendar. Veja rapidamente sua agenda de eventos.',
        },
        {
          icon: '📝',
          title: 'Contrato de assessoria',
          description: 'Termos claros sobre escopo, entregas, cronograma e política de cancelamento.',
        },
        {
          icon: '📦',
          title: 'Pacotes de assessoria',
          description: 'Completa, parcial ou do dia. Add-ons para lua de mel, cerimonial extra, etc.',
        },
      ],
      packages: [
        { name: 'Assessoria do Dia', description: 'Coordenação no dia do evento' },
        { name: 'Assessoria Parcial', description: '3 meses antes + dia do evento' },
        { name: 'Assessoria Completa', description: 'Do noivado ao casamento' },
      ],
      testimonial: {
        quote: 'Minhas noivas amam a praticidade de escolher o pacote e já pagar o sinal pelo link. Eu confirmo a data e começo o planejamento.',
        author: 'Fernanda Lima',
        role: 'Wedding Planner',
        avatar: 'https://randomuser.me/api/portraits/women/3.jpg',
      },
      faq: [
        {
          question: 'Como funciona para wedding planners?',
          answer: 'Você cria pacotes de assessoria (do dia, parcial, completa), define o sinal e envia o link. A noiva escolhe, aceita o contrato e paga. A data do casamento é bloqueada.',
        },
        {
          question: 'Posso parcelar o valor total?',
          answer: 'Sim! No plano Pro você pode parcelar. Ideal para assessoria completa que tem valor mais alto.',
        },
        {
          question: 'Como funciona o contrato?',
          answer: 'Configure os termos uma vez: escopo, entregas, cronograma, cancelamento. A noiva aceita antes de pagar e você tem registro legal.',
        },
        {
          question: 'Funciona para outros eventos?',
          answer: 'Sim! Funciona para casamentos, formaturas, aniversários de 15 anos, eventos corporativos — qualquer assessoria de evento.',
        },
      ],
    },
    en: {
      slug: 'wedding-planners',
      icon: '💒',
      color: 'rose',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699818/Gemini_Generated_Image_e484d8e484d8e484_mplgnd.png',
          alt: 'Wedding planner organizing wedding',
          caption: 'Integrate with your Google Calendar to manage your dates',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699814/Gemini_Generated_Image_e484d8e484d8e484_1_aymixd.png', alt: 'Using Pinday Mobile', caption: 'Easily organize weddings using Pinday Mobile' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699810/Gemini_Generated_Image_e484d8e484d8e484_2_iy3eek.png', alt: 'Using Metrics Dashboard', caption: 'Track your bookings and payments all in one place' },
        ],
      },
      hero: {
        badge: 'For Wedding Planners',
        title: 'Deposit that holds the wedding date',
        subtitle: 'Full, partial or day-of planning with professional contract. Calendar synced automatically when bride pays.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Deposit that secures the date',
          description: 'Bride pays the deposit and you block the date. Wedding booked, no risk of losing to competition.',
        },
        {
          icon: '📅',
          title: 'Event calendar',
          description: 'Each wedding goes on your Google Calendar. Quickly see your event schedule.',
        },
        {
          icon: '📝',
          title: 'Planning contract',
          description: 'Clear terms about scope, deliverables, timeline and cancellation policy.',
        },
        {
          icon: '📦',
          title: 'Planning packages',
          description: 'Full, partial or day-of. Add-ons for honeymoon, extra ceremony, etc.',
        },
      ],
      packages: [
        { name: 'Day-of Coordination', description: 'Coordination on the event day' },
        { name: 'Partial Planning', description: '3 months before + event day' },
        { name: 'Full Planning', description: 'From engagement to wedding' },
      ],
      testimonial: {
        quote: 'My brides love the convenience of choosing the package and paying the deposit through the link. I confirm the date and start planning.',
        author: 'Jennifer White',
        role: 'Wedding Planner',
        avatar: 'https://randomuser.me/api/portraits/women/11.jpg',
      },
      faq: [
        {
          question: 'How does it work for wedding planners?',
          answer: 'You create planning packages (day-of, partial, full), set the deposit and send the link. Bride chooses, accepts contract and pays. Wedding date is blocked.',
        },
        {
          question: 'Can I offer installments for the full amount?',
          answer: 'Yes! On Pro plan you can offer installments. Ideal for full planning with higher value.',
        },
        {
          question: 'How does the contract work?',
          answer: 'Configure terms once: scope, deliverables, timeline, cancellation. Bride accepts before paying and you have legal record.',
        },
        {
          question: 'Does it work for other events?',
          answer: 'Yes! Works for weddings, graduations, quinceañeras, corporate events — any event planning.',
        },
      ],
    },
    es: {
      slug: 'wedding-planners',
      icon: '💒',
      color: 'rose',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699818/Gemini_Generated_Image_e484d8e484d8e484_mplgnd.png',
          alt: 'Wedding planner organizando boda',
          caption: 'Integra con tu Google Calendar para gestionar tus fechas',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699814/Gemini_Generated_Image_e484d8e484d8e484_1_aymixd.png', alt: 'Decoración de boda', caption: 'Organiza bodas fácilmente usando Pinday Mobile' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699810/Gemini_Generated_Image_e484d8e484d8e484_2_iy3eek.png', alt: 'Ceremonia de boda', caption: 'Gestiona tus reservas y pagos en un solo lugar' },
        ],
      },
      hero: {
        badge: 'Para Wedding Planners',
        title: 'Seña que reserva la fecha de la boda',
        subtitle: 'Asesoría completa, parcial o del día con contrato profesional. Calendario sincronizado automáticamente cuando la novia paga.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Seña que asegura la fecha',
          description: 'La novia paga la seña y tú bloqueas la fecha. Boda reservada, sin riesgo de perder ante la competencia.',
        },
        {
          icon: '📅',
          title: 'Calendario de eventos',
          description: 'Cada boda queda en tu Google Calendar. Ve rápidamente tu agenda de eventos.',
        },
        {
          icon: '📝',
          title: 'Contrato de asesoría',
          description: 'Términos claros sobre alcance, entregas, cronograma y política de cancelación.',
        },
        {
          icon: '📦',
          title: 'Paquetes de asesoría',
          description: 'Completa, parcial o del día. Add-ons para luna de miel, ceremonial extra, etc.',
        },
      ],
      packages: [
        { name: 'Asesoría del Día', description: 'Coordinación el día del evento' },
        { name: 'Asesoría Parcial', description: '3 meses antes + día del evento' },
        { name: 'Asesoría Completa', description: 'Del compromiso a la boda' },
      ],
      testimonial: {
        quote: 'Mis novias aman la practicidad de elegir el paquete y pagar la seña por el link. Yo confirmo la fecha y empiezo la planificación.',
        author: 'María Fernanda',
        role: 'Wedding Planner',
        avatar: 'https://randomuser.me/api/portraits/women/21.jpg',
      },
      faq: [
        {
          question: '¿Cómo funciona para wedding planners?',
          answer: 'Creas paquetes de asesoría (del día, parcial, completa), defines la seña y envías el link. La novia elige, acepta el contrato y paga. La fecha de la boda se bloquea.',
        },
        {
          question: '¿Puedo ofrecer cuotas por el monto total?',
          answer: '¡Sí! En el plan Pro puedes ofrecer cuotas. Ideal para asesoría completa con valor más alto.',
        },
        {
          question: '¿Cómo funciona el contrato?',
          answer: 'Configuras los términos una vez: alcance, entregas, cronograma, cancelación. La novia acepta antes de pagar y tienes registro legal.',
        },
        {
          question: '¿Funciona para otros eventos?',
          answer: '¡Sí! Funciona para bodas, graduaciones, quinceañeras, eventos corporativos — cualquier asesoría de evento.',
        },
      ],
    },
  },

  'personal-organizers': {
    pt: {
      slug: 'personal-organizers',
      icon: '🏠',
      color: 'teal',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700353/Gemini_Generated_Image_6sdk4d6sdk4d6sdk_gls87r.png',
          alt: 'Personal organizer organizando ambiente',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700457/Gemini_Generated_Image_90t1uj90t1uj90t1_d9zmv2.png', alt: 'Organização de closet' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700462/Gemini_Generated_Image_t12eygt12eygt12e_ardjcq.png', alt: 'Organização de cozinha' },
        ],
      },
      hero: {
        badge: 'Para Personal Organizers',
        title: 'Sinal para projetos de organização',
        subtitle: 'Reserve projetos de organização residencial ou comercial. Pacotes por ambiente ou projeto completo com contratos e políticas claras.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Sinal que confirma o projeto',
          description: 'O cliente paga o sinal e você já pode reservar os dias do projeto na agenda.',
        },
        {
          icon: '📅',
          title: 'Datas do projeto no calendário',
          description: 'Bloqueie os dias do projeto no Google Calendar assim que o cliente paga.',
        },
        {
          icon: '📝',
          title: 'Contrato de organização',
          description: 'Termos sobre escopo, descarte de itens, privacidade e política de cancelamento.',
        },
        {
          icon: '📦',
          title: 'Pacotes flexíveis',
          description: 'Por ambiente (closet, cozinha, home office) ou projeto completo. Add-ons para manutenção.',
        },
      ],
      packages: [
        { name: 'Ambiente Único', description: '1 cômodo, até 6 horas' },
        { name: 'Pacote Casa', description: 'Até 4 ambientes, 2 dias' },
        { name: 'Projeto Completo', description: 'Casa inteira + manutenção trimestral' },
      ],
      testimonial: {
        quote: 'Meus clientes pagam o sinal e eu já reservo os dias na agenda. Sem aquela incerteza de "vai confirmar ou não".',
        author: 'Paula Mendes',
        role: 'Personal Organizer',
        avatar: 'https://randomuser.me/api/portraits/women/4.jpg',
      },
      faq: [
        {
          question: 'Como funciona para personal organizers?',
          answer: 'Você cria pacotes (por ambiente ou projeto), define o sinal e envia o link. O cliente escolhe, aceita os termos e paga. Os dias do projeto são bloqueados.',
        },
        {
          question: 'Posso incluir termos sobre descarte?',
          answer: 'Sim! Configure termos sobre descarte de itens, doação, privacidade e o que acontece com objetos encontrados.',
        },
        {
          question: 'Funciona para projetos comerciais?',
          answer: 'Sim! Funciona para organização residencial e comercial. Escritórios, consultórios, lojas.',
        },
        {
          question: 'Como cobrar manutenção?',
          answer: 'Você pode criar add-ons para manutenção mensal ou trimestral. O cliente adiciona ao pacote principal.',
        },
      ],
    },
    en: {
      slug: 'personal-organizers',
      icon: '🏠',
      color: 'teal',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700353/Gemini_Generated_Image_6sdk4d6sdk4d6sdk_gls87r.png',
          alt: 'Personal organizer organizing space',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700457/Gemini_Generated_Image_90t1uj90t1uj90t1_d9zmv2.png', alt: 'Closet organization' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700462/Gemini_Generated_Image_t12eygt12eygt12e_ardjcq.png', alt: 'Kitchen organization' },
        ],
      },
      hero: {
        badge: 'For Personal Organizers',
        title: 'Deposit for organizing projects',
        subtitle: 'Book residential or commercial organizing projects. Per-room or full-project packages with contracts and clear policies.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Deposit that confirms project',
          description: 'Client pays the deposit and you can already reserve the project days on your schedule.',
        },
        {
          icon: '📅',
          title: 'Project dates on calendar',
          description: 'Block the project days on Google Calendar as soon as client pays.',
        },
        {
          icon: '📝',
          title: 'Organizing contract',
          description: 'Terms about scope, item disposal, privacy and cancellation policy.',
        },
        {
          icon: '📦',
          title: 'Flexible packages',
          description: 'Per-room (closet, kitchen, home office) or full project. Add-ons for maintenance.',
        },
      ],
      packages: [
        { name: 'Single Room', description: '1 room, up to 6 hours' },
        { name: 'Home Package', description: 'Up to 4 rooms, 2 days' },
        { name: 'Full Project', description: 'Entire home + quarterly maintenance' },
      ],
      testimonial: {
        quote: 'My clients pay the deposit and I already reserve the days on my schedule. No more uncertainty about confirmations.',
        author: 'Sarah Clark',
        role: 'Personal Organizer',
        avatar: 'https://randomuser.me/api/portraits/women/12.jpg',
      },
      faq: [
        {
          question: 'How does it work for personal organizers?',
          answer: 'You create packages (per-room or project), set the deposit and send the link. Client chooses, accepts terms and pays. Project days are blocked.',
        },
        {
          question: 'Can I include terms about disposal?',
          answer: 'Yes! Configure terms about item disposal, donation, privacy and what happens with found items.',
        },
        {
          question: 'Does it work for commercial projects?',
          answer: 'Yes! Works for residential and commercial organizing. Offices, clinics, stores.',
        },
        {
          question: 'How to charge for maintenance?',
          answer: 'You can create add-ons for monthly or quarterly maintenance. Client adds to the main package.',
        },
      ],
    },
    es: {
      slug: 'personal-organizers',
      icon: '🏠',
      color: 'teal',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700353/Gemini_Generated_Image_6sdk4d6sdk4d6sdk_gls87r.png',
          alt: 'Personal organizer organizando espacio',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700457/Gemini_Generated_Image_90t1uj90t1uj90t1_d9zmv2.png', alt: 'Organización de closet' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767700462/Gemini_Generated_Image_t12eygt12eygt12e_ardjcq.png', alt: 'Organización de cocina' },
        ],
      },
      hero: {
        badge: 'Para Personal Organizers',
        title: 'Seña para proyectos de organización',
        subtitle: 'Reserva proyectos de organización residencial o comercial. Paquetes por ambiente o proyecto completo con contratos y políticas claras.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Seña que confirma el proyecto',
          description: 'El cliente paga la seña y ya puedes reservar los días del proyecto en tu agenda.',
        },
        {
          icon: '📅',
          title: 'Fechas del proyecto en calendario',
          description: 'Bloquea los días del proyecto en Google Calendar en cuanto el cliente paga.',
        },
        {
          icon: '📝',
          title: 'Contrato de organización',
          description: 'Términos sobre alcance, descarte de ítems, privacidad y política de cancelación.',
        },
        {
          icon: '📦',
          title: 'Paquetes flexibles',
          description: 'Por ambiente (closet, cocina, home office) o proyecto completo. Add-ons para mantenimiento.',
        },
      ],
      packages: [
        { name: 'Ambiente Único', description: '1 habitación, hasta 6 horas' },
        { name: 'Paquete Casa', description: 'Hasta 4 ambientes, 2 días' },
        { name: 'Proyecto Completo', description: 'Casa entera + mantenimiento trimestral' },
      ],
      testimonial: {
        quote: 'Mis clientes pagan la seña y yo ya reservo los días en mi agenda. Sin esa incertidumbre de "¿confirma o no?".',
        author: 'Paula Méndez',
        role: 'Personal Organizer',
        avatar: 'https://randomuser.me/api/portraits/women/22.jpg',
      },
      faq: [
        {
          question: '¿Cómo funciona para personal organizers?',
          answer: 'Creas paquetes (por ambiente o proyecto), defines la seña y envías el link. El cliente elige, acepta los términos y paga. Los días del proyecto se bloquean.',
        },
        {
          question: '¿Puedo incluir términos sobre descarte?',
          answer: '¡Sí! Configura términos sobre descarte de ítems, donación, privacidad y qué pasa con objetos encontrados.',
        },
        {
          question: '¿Funciona para proyectos comerciales?',
          answer: '¡Sí! Funciona para organización residencial y comercial. Oficinas, consultorios, tiendas.',
        },
        {
          question: '¿Cómo cobrar mantenimiento?',
          answer: 'Puedes crear add-ons para mantenimiento mensual o trimestral. El cliente lo agrega al paquete principal.',
        },
      ],
    },
  },

  'event-planners': {
    pt: {
      slug: 'event-planners',
      icon: '🥂',
      color: 'indigo',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767701953/Gemini_Generated_Image_q2vmcvq2vmcvq2vm_xjfh7h.png',
          alt: 'Organizador de eventos planejando um evento',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767701949/Gemini_Generated_Image_q2vmcvq2vmcvq2vm_1_ou0jbs.png', alt: 'Organização de evento corporativo' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767702179/udreeea_nlo4oy.jpg', alt: 'Decoração de festa' },
        ],
      },
      hero: {
        badge: 'Para Organizadores de Eventos',
        title: 'Sinal para festas e eventos corporativos',
        subtitle: 'Garanta a data do evento com um sinal. Contratos para festas de aniversário, eventos corporativos e lançamentos.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Reserva de data garantida',
          description: 'O cliente paga o sinal e a data é sua. Evite bloqueios falsos na agenda.',
        },
        {
          icon: '📅',
          title: 'Sincronização automática',
          description: 'Data do evento vai direto para o Google Calendar após o pagamento.',
        },
        {
          icon: '📝',
          title: 'Contrato de prestação',
          description: 'Defina responsabilidades, horários e políticas de cancelamento no contrato digital.',
        },
        {
          icon: '📦',
          title: 'Pacotes personalizados',
          description: 'Crie pacotes para diferentes tipos de eventos e tamanhos de público.',
        },
      ],
      packages: [
        { name: 'Festa Infantil', description: 'Decoração + coordenação de 4h' },
        { name: 'Evento Corporativo', description: 'Planejamento completo e execução' },
        { name: 'Lançamento de Produto', description: 'Assessoria de imprensa e evento' },
      ],
      testimonial: {
        quote: 'Facilitou muito o fechamento de contratos corporativos. O link passa profissionalismo e agilidade.',
        author: 'Roberto Alves',
        role: 'Produtor de Eventos',
        avatar: 'https://randomuser.me/api/portraits/men/2.jpg',
      },
      faq: [
        {
          question: 'Serve para eventos corporativos?',
          answer: 'Sim, é ideal para garantir a data de eventos de empresas, workshops e confraternizações.',
        },
        {
          question: 'Posso emitir nota fiscal?',
          answer: 'O sistema gerencia o pagamento. A nota fiscal você emite normalmente para o seu cliente.',
        },
      ],
    },
    en: {
      slug: 'event-planners',
      icon: '🥂',
      color: 'indigo',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767701953/Gemini_Generated_Image_q2vmcvq2vmcvq2vm_xjfh7h.png',
          alt: 'Event planner organizing an event',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767701949/Gemini_Generated_Image_q2vmcvq2vmcvq2vm_1_ou0jbs.png', alt: 'Corporate event organization' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767702179/udreeea_nlo4oy.jpg  ', alt: 'Party decoration' },
        ],
      },
      hero: {
        badge: 'For Event Planners',
        title: 'Deposit for parties and corporate events',
        subtitle: 'Secure the event date with a deposit. Contracts for birthday parties, corporate events and launches.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Guaranteed date booking',
          description: 'Client pays the deposit and the date is yours. Avoid false blocks on your schedule.',
        },
        {
          icon: '📅',
          title: 'Automatic sync',
          description: 'Event date goes straight to Google Calendar after payment.',
        },
        {
          icon: '📝',
          title: 'Service contract',
          description: 'Define responsibilities, schedules and cancellation policies in the digital contract.',
        },
        {
          icon: '📦',
          title: 'Custom packages',
          description: 'Create packages for different event types and audience sizes.',
        },
      ],
      packages: [
        { name: 'Kids Party', description: 'Decor + 4h coordination' },
        { name: 'Corporate Event', description: 'Full planning and execution' },
        { name: 'Product Launch', description: 'PR and event management' },
      ],
      testimonial: {
        quote: 'It made closing corporate contracts much easier. The link conveys professionalism and agility.',
        author: 'Robert Smith',
        role: 'Event Producer',
        avatar: 'https://randomuser.me/api/portraits/men/5.jpg',
      },
      faq: [
        {
          question: 'Is it good for corporate events?',
          answer: 'Yes, it is ideal for securing dates for company events, workshops and gatherings.',
        },
        {
          question: 'Can I issue invoices?',
          answer: 'The system manages the payment. You issue the invoice to your client as usual.',
        },
      ],
    },
    es: {
      slug: 'event-planners',
      icon: '🥂',
      color: 'indigo',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767701953/Gemini_Generated_Image_q2vmcvq2vmcvq2vm_xjfh7h.png',
          alt: 'Organizador de eventos planificando un evento',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767701949/Gemini_Generated_Image_q2vmcvq2vmcvq2vm_1_ou0jbs.png', alt: 'Organización de evento corporativo' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767702179/udreeea_nlo4oy.jpg  ', alt: 'Decoración de fiesta' },
        ],
      },
      hero: {
        badge: 'Para Organizadores de Eventos',
        title: 'Seña para fiestas y eventos corporativos',
        subtitle: 'Asegura la fecha del evento con una seña. Contratos para cumpleaños, eventos corporativos y lanzamientos.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Reserva de fecha garantizada',
          description: 'El cliente paga la seña y la fecha es tuya. Evita bloqueos falsos en la agenda.',
        },
        {
          icon: '📅',
          title: 'Sincronización automática',
          description: 'La fecha del evento va directo a Google Calendar después del pago.',
        },
        {
          icon: '📝',
          title: 'Contrato de servicio',
          description: 'Define responsabilidades, horarios y políticas de cancelación en el contrato digital.',
        },
        {
          icon: '📦',
          title: 'Paquetes personalizados',
          description: 'Crea paquetes para diferentes tipos de eventos y tamaños de público.',
        },
      ],
      packages: [
        { name: 'Fiesta Infantil', description: 'Decoración + coordinación de 4h' },
        { name: 'Evento Corporativo', description: 'Planificación completa y ejecución' },
        { name: 'Lanzamiento de Producto', description: 'Prensa y gestión del evento' },
      ],
      testimonial: {
        quote: 'Facilitó mucho el cierre de contratos corporativos. El link transmite profesionalismo y agilidad.',
        author: 'Roberto Álvarez',
        role: 'Productor de Eventos',
        avatar: 'https://randomuser.me/api/portraits/men/9.jpg',
      },
      faq: [
        {
          question: '¿Sirve para eventos corporativos?',
          answer: 'Sí, es ideal para asegurar fechas de eventos de empresas, workshops y reuniones.',
        },
        {
          question: '¿Puedo emitir factura?',
          answer: 'El sistema gestiona el pago. La factura la emites tú normalmente a tu cliente.',
        },
      ],
    },
  },

  'yoga-instructors': {
    pt: {
      slug: 'yoga-instructors',
      icon: '🧘',
      color: 'emerald',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703097/Gemini_Generated_Image_b7ux9ab7ux9ab7ux_ijna5n.png',
          alt: 'Instrutor de yoga ensinando aula',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703095/Gemini_Generated_Image_b7ux9ab7ux9ab7ux_1_c65rje.png', alt: 'Prática de yoga em grupo' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703157/udreeea2_vo85v0.jpg', alt: 'Sessão de meditação guiada' },
        ],
      },
      hero: {
        badge: 'Para Instrutores de Yoga',
        title: 'Sinal para aulas particulares e retiros',
        subtitle: 'Garanta a presença dos alunos em aulas privadas ou workshops. Receba antecipado e evite cancelamentos de última hora.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Pagamento antecipado',
          description: 'Receba o valor da aula ou sinal do retiro antes da data. Compromisso financeiro do aluno.',
        },
        {
          icon: '📅',
          title: 'Agenda organizada',
          description: 'Aulas confirmadas aparecem no seu Google Calendar automaticamente.',
        },
        {
          icon: '📝',
          title: 'Termo de responsabilidade',
          description: 'Inclua questionário de saúde e isenção de responsabilidade no processo de pagamento.',
        },
        {
          icon: '📦',
          title: 'Pacotes de aulas',
          description: 'Venda aulas avulsas, pacotes mensais ou vagas em workshops.',
        },
      ],
      packages: [
        { name: 'Aula Avulsa', description: '1h de prática personalizada' },
        { name: 'Pacote Mensal', description: '8 aulas (2x na semana)' },
        { name: 'Workshop', description: 'Imersão de final de semana' },
      ],
      testimonial: {
        quote: 'Antes eu levava bolo nas aulas particulares. Agora o aluno paga antes e o compromisso é sério.',
        author: 'Camila Zen',
        role: 'Instrutora de Yoga',
        avatar: 'https://randomuser.me/api/portraits/women/5.jpg',
      },
      faq: [
        {
          question: 'Funciona para aulas online?',
          answer: 'Sim! Você pode configurar para enviar o link do Zoom automaticamente após o pagamento.',
        },
        {
          question: 'Posso vender pacotes?',
          answer: 'Sim, você pode criar pacotes de 5 ou 10 aulas com desconto.',
        },
      ],
    },
    en: {
      slug: 'yoga-instructors',
      icon: '🧘',
      color: 'emerald',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703097/Gemini_Generated_Image_b7ux9ab7ux9ab7ux_ijna5n.png',
          alt: 'Yoga instructor teaching class',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703095/Gemini_Generated_Image_b7ux9ab7ux9ab7ux_1_c65rje.png', alt: 'Group yoga practice' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703157/udreeea2_vo85v0.jpg', alt: 'Guided meditation session' },
        ],
      },
      hero: {
        badge: 'For Yoga Instructors',
        title: 'Deposit for private classes and retreats',
        subtitle: 'Ensure student attendance in private classes or workshops. Get paid upfront and avoid last-minute cancellations.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Upfront payment',
          description: 'Receive the class fee or retreat deposit before the date. Financial commitment from the student.',
        },
        {
          icon: '📅',
          title: 'Organized schedule',
          description: 'Confirmed classes appear on your Google Calendar automatically.',
        },
        {
          icon: '📝',
          title: 'Liability waiver',
          description: 'Include health questionnaire and liability waiver in the payment process.',
        },
        {
          icon: '📦',
          title: 'Class packages',
          description: 'Sell single classes, monthly packages or workshop spots.',
        },
      ],
      packages: [
        { name: 'Single Class', description: '1h personalized practice' },
        { name: 'Monthly Package', description: '8 classes (2x a week)' },
        { name: 'Workshop', description: 'Weekend immersion' },
      ],
      testimonial: {
        quote: 'I used to get ghosted on private classes. Now the student pays before and the commitment is real.',
        author: 'Camila Zen',
        role: 'Yoga Instructor',
        avatar: 'https://randomuser.me/api/portraits/women/13.jpg',
      },
      faq: [
        {
          question: 'Does it work for online classes?',
          answer: 'Yes! You can configure it to send the Zoom link automatically after payment.',
        },
        {
          question: 'Can I sell packages?',
          answer: 'Yes, you can create 5 or 10-class packages with a discount.',
        },
      ],
    },
    es: {
      slug: 'yoga-instructors',
      icon: '🧘',
      color: 'emerald',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703097/Gemini_Generated_Image_b7ux9ab7ux9ab7ux_ijna5n.png',
          alt: 'Instructor de yoga enseñando clase',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703095/Gemini_Generated_Image_b7ux9ab7ux9ab7ux_1_c65rje.png', alt: 'Práctica de yoga en grupo' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767703157/udreeea2_vo85v0.jpg', alt: 'Sesión de meditación guiada' },
        ],
      },
      hero: {
        badge: 'Para Instructores de Yoga',
        title: 'Seña para clases particulares y retiros',
        subtitle: 'Asegura la presencia de alumnos en clases privadas o talleres. Cobra por adelantado y evita cancelaciones de última hora.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Pago anticipado',
          description: 'Recibe el valor de la clase o seña del retiro antes de la fecha. Compromiso financiero del alumno.',
        },
        {
          icon: '📅',
          title: 'Agenda organizada',
          description: 'Las clases confirmadas aparecen en tu Google Calendar automáticamente.',
        },
        {
          icon: '📝',
          title: 'Exención de responsabilidad',
          description: 'Incluye cuestionario de salud y exención de responsabilidad en el proceso de pago.',
        },
        {
          icon: '📦',
          title: 'Paquetes de clases',
          description: 'Vende clases sueltas, paquetes mensuales o cupos en talleres.',
        },
      ],
      packages: [
        { name: 'Clase Suelta', description: '1h de práctica personalizada' },
        { name: 'Paquete Mensual', description: '8 clases (2x por semana)' },
        { name: 'Taller', description: 'Inmersión de fin de semana' },
      ],
      testimonial: {
        quote: 'Antes me cancelaban clases particulares sobre la hora. Ahora el alumno paga antes y el compromiso es serio.',
        author: 'Camila Zen',
        role: 'Instructora de Yoga',
        avatar: 'https://randomuser.me/api/portraits/women/23.jpg',
      },
      faq: [
        {
          question: '¿Funciona para clases online?',
          answer: '¡Sí! Puedes configurarlo para enviar el link de Zoom automáticamente después del pago.',
        },
        {
          question: '¿Puedo vender paquetes?',
          answer: 'Sí, puedes crear paquetes de 5 o 10 clases con descuento.',
        },
      ],
    },
  },

  'fitness-coaches': {
    pt: {
      slug: 'fitness-coaches',
      icon: '💪',
      color: 'red',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706971/Gemini_Generated_Image_9yjnur9yjnur9yjn_eg6qzt.png',
          alt: 'Personal trainer treinando cliente',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706973/Gemini_Generated_Image_9yjnur9yjnur9yjn_1_kb2dbk.png', alt: 'Sessão de treino personalizado' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706969/Gemini_Generated_Image_9yjnur9yjnur9yjn_2_azoi8b.png', alt: 'Avaliação física' },
        ],
      },
      hero: {
        badge: 'Para Personal Trainers',
        title: 'Sinal para consultoria e aulas',
        subtitle: 'Venda consultorias online, avaliações físicas e pacotes de aulas. Receba antes de montar o treino.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Pagamento garantido',
          description: 'Não monte treinos sem receber. O aluno paga e você recebe a notificação para começar o trabalho.',
        },
        {
          icon: '📅',
          title: 'Agendamento de avaliação',
          description: 'O aluno paga e já agenda a avaliação física ou call de alinhamento no seu calendário.',
        },
        {
          icon: '📝',
          title: 'Anamnese inclusa',
          description: 'Configure perguntas de saúde e objetivos que o aluno responde ao pagar.',
        },
        {
          icon: '📦',
          title: 'Planos variados',
          description: 'Consultoria mensal, trimestral ou aula presencial avulsa.',
        },
      ],
      packages: [
        { name: 'Consultoria Online', description: 'Treino via app + suporte WhatsApp' },
        { name: 'Aula Presencial', description: '1h de treino personalizado' },
        { name: 'Avaliação Física', description: 'Bioimpedância e dobras cutâneas' },
      ],
      testimonial: {
        quote: 'Acabou o problema de enviar o treino e ficar cobrando o aluno depois. Agora só envio quando o pagamento cai.',
        author: 'Felipe Monstro',
        role: 'Personal Trainer',
        avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
      },
      faq: [
        {
          question: 'Serve para consultoria online?',
          answer: 'Perfeito para isso. Você vende o plano, o aluno paga e responde a anamnese, e você entrega o treino.',
        },
        {
          question: 'Posso cobrar recorrente?',
          answer: 'Sim, você pode criar assinaturas mensais para seus alunos de consultoria.',
        },
      ],
    },
    en: {
      slug: 'fitness-coaches',
      icon: '💪',
      color: 'red',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706971/Gemini_Generated_Image_9yjnur9yjnur9yjn_eg6qzt.png',
          alt: 'Fitness coach training client',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706973/Gemini_Generated_Image_9yjnur9yjnur9yjn_1_kb2dbk.png', alt: 'Personalized training session' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706969/Gemini_Generated_Image_9yjnur9yjnur9yjn_2_azoi8b.png', alt: 'Fitness assessment' },
        ],
      },
      hero: {
        badge: 'For Fitness Coaches',
        title: 'Deposit for consulting and training',
        subtitle: 'Sell online coaching, fitness assessments and training packages. Get paid before designing the workout.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Guaranteed payment',
          description: 'Don\'t design workouts for free. Student pays and you get notified to start working.',
        },
        {
          icon: '📅',
          title: 'Assessment booking',
          description: 'Student pays and books the fitness assessment or alignment call on your calendar.',
        },
        {
          icon: '📝',
          title: 'Anamnesis included',
          description: 'Configure health and goal questions that the student answers when paying.',
        },
        {
          icon: '📦',
          title: 'Varied plans',
          description: 'Monthly, quarterly coaching or single in-person session.',
        },
      ],
      packages: [
        { name: 'Online Coaching', description: 'App workout + WhatsApp support' },
        { name: 'In-Person Session', description: '1h personalized training' },
        { name: 'Fitness Assessment', description: 'Body composition analysis' },
      ],
      testimonial: {
        quote: 'No more sending workouts and chasing payments later. Now I only send it when the payment clears.',
        author: 'Phil Strong',
        role: 'Personal Trainer',
        avatar: 'https://randomuser.me/api/portraits/men/6.jpg',
      },
      faq: [
        {
          question: 'Is it good for online coaching?',
          answer: 'Perfect for it. You sell the plan, student pays and answers the anamnesis, and you deliver the workout.',
        },
        {
          question: 'Can I charge recurringly?',
          answer: 'Yes, you can create monthly subscriptions for your coaching students.',
        },
      ],
    },
    es: {
      slug: 'fitness-coaches',
      icon: '💪',
      color: 'red',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706971/Gemini_Generated_Image_9yjnur9yjnur9yjn_eg6qzt.png',
          alt: 'Entrenador personal entrenando cliente',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706973/Gemini_Generated_Image_9yjnur9yjnur9yjn_1_kb2dbk.png', alt: 'Sesión de entrenamiento personalizado' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767706969/Gemini_Generated_Image_9yjnur9yjnur9yjn_2_azoi8b.png', alt: 'Evaluación física' },
        ],
      },
      hero: {
        badge: 'Para Entrenadores Personales',
        title: 'Seña para consultoría y entrenamiento',
        subtitle: 'Vende asesorías online, evaluaciones físicas y paquetes de clases. Cobra antes de armar la rutina.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Pago garantizado',
          description: 'No armes rutinas sin cobrar. El alumno paga y recibes la notificación para empezar el trabajo.',
        },
        {
          icon: '📅',
          title: 'Reserva de evaluación',
          description: 'El alumno paga y ya agenda la evaluación física o llamada de alineación en tu calendario.',
        },
        {
          icon: '📝',
          title: 'Anamnesis incluida',
          description: 'Configura preguntas de salud y objetivos que el alumno responde al pagar.',
        },
        {
          icon: '📦',
          title: 'Planes variados',
          description: 'Asesoría mensual, trimestral o clase presencial suelta.',
        },
      ],
      packages: [
        { name: 'Asesoría Online', description: 'Rutina por app + soporte WhatsApp' },
        { name: 'Clase Presencial', description: '1h de entrenamiento personalizado' },
        { name: 'Evaluación Física', description: 'Bioimpedancia y pliegues' },
      ],
      testimonial: {
        quote: 'Se acabó el problema de enviar la rutina y perseguir el pago después. Ahora solo envío cuando el pago entra.',
        author: 'Felipe Fuerte',
        role: 'Entrenador Personal',
        avatar: 'https://randomuser.me/api/portraits/men/10.jpg',
      },
      faq: [
        {
          question: '¿Sirve para asesoría online?',
          answer: 'Perfecto para eso. Vendes el plan, el alumno paga y responde la anamnesis, y tú entregas la rutina.',
        },
        {
          question: '¿Puedo cobrar recurrente?',
          answer: 'Sí, puedes crear suscripciones mensuales para tus alumnos de asesoría.',
        },
      ],
    },
  },

  'hair-stylists': {
    pt: {
      slug: 'hair-stylists',
      icon: '💇',
      color: 'pink',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Cabeleireiro trabalhando em cliente',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Procedimento de coloração' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Transformação de cabelo' },
        ],
      },
      hero: {
        badge: 'Para Cabeleireiros',
        title: 'Sinal para mechas e procedimentos longos',
        subtitle: 'Não perca horas da sua agenda com faltas. Cobre um sinal para reservar horários de mechas, alisamentos e mega hair.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Fim dos furos na agenda',
          description: 'Procedimentos longos exigem compromisso. O sinal garante que a cliente vai comparecer.',
        },
        {
          icon: '📅',
          title: 'Reserva automática',
          description: 'Cliente paga o sinal e o horário de 4-6 horas fica bloqueado no seu calendário.',
        },
        {
          icon: '📝',
          title: 'Teste de mecha',
          description: 'Inclua termos sobre a necessidade de teste de mecha e saúde do fio antes do procedimento.',
        },
        {
          icon: '📦',
          title: 'Serviços complexos',
          description: 'Ideal para loiros, colorações, alisamentos e colocações de extensão.',
        },
      ],
      packages: [
        { name: 'Mechas Criativas', description: 'Técnica personalizada + tratamento' },
        { name: 'Alisamento', description: 'Progressiva ou botox capilar' },
        { name: 'Mega Hair', description: 'Aplicação ou manutenção de fita' },
      ],
      testimonial: {
        quote: 'Perdia muito dinheiro quando uma cliente de mechas faltava. Com o sinal, elas não faltam mais.',
        author: 'Jéssica Hair',
        role: 'Cabeleireira',
        avatar: 'https://randomuser.me/api/portraits/women/6.jpg',
      },
      faq: [
        {
          question: 'O valor do sinal é descontado no final?',
          answer: 'Sim, o sinal serve como reserva. No dia, a cliente paga apenas o restante do valor.',
        },
        {
          question: 'E se o cabelo não passar no teste?',
          answer: 'Você pode definir na política que, caso não passe no teste, o valor pode ser convertido em tratamento.',
        },
      ],
    },
    en: {
      slug: 'hair-stylists',
      icon: '💇',
      color: 'pink',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Hair stylist working on client',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Hair coloring procedure' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Hair transformation' },
        ],
      },
      hero: {
        badge: 'For Hair Stylists',
        title: 'Deposit for highlights and long services',
        subtitle: 'Don\'t lose hours of your schedule to no-shows. Charge a deposit to book highlights, straightening and extensions.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'End of schedule gaps',
          description: 'Long procedures require commitment. The deposit ensures the client will show up.',
        },
        {
          icon: '📅',
          title: 'Automatic booking',
          description: 'Client pays deposit and the 4-6 hour slot is blocked on your calendar.',
        },
        {
          icon: '📝',
          title: 'Strand test',
          description: 'Include terms about the need for strand test and hair health before the procedure.',
        },
        {
          icon: '📦',
          title: 'Complex services',
          description: 'Ideal for blondes, coloring, straightening and extension applications.',
        },
      ],
      packages: [
        { name: 'Creative Highlights', description: 'Custom technique + treatment' },
        { name: 'Straightening', description: 'Brazilian blowout or botox' },
        { name: 'Extensions', description: 'Tape-in application or maintenance' },
      ],
      testimonial: {
        quote: 'I lost a lot of money when a highlights client didn\'t show up. With the deposit, they don\'t miss anymore.',
        author: 'Jessica Hair',
        role: 'Hair Stylist',
        avatar: 'https://randomuser.me/api/portraits/women/14.jpg',
      },
      faq: [
        {
          question: 'Is the deposit deducted at the end?',
          answer: 'Yes, the deposit serves as a reservation. On the day, the client pays only the remaining amount.',
        },
        {
          question: 'What if the hair doesn\'t pass the test?',
          answer: 'You can define in the policy that if it fails the test, the amount can be converted into a treatment.',
        },
      ],
    },
    es: {
      slug: 'hair-stylists',
      icon: '💇',
      color: 'pink',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Estilista trabajando con cliente',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Procedimiento de coloración' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Transformación de cabello' },
        ],
      },
      hero: {
        badge: 'Para Estilistas',
        title: 'Seña para mechas y procedimientos largos',
        subtitle: 'No pierdas horas de tu agenda con faltas. Cobra una seña para reservar horarios de mechas, alisados y extensiones.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Fin de los huecos en agenda',
          description: 'Procedimientos largos exigen compromiso. La seña garantiza que la clienta asistirá.',
        },
        {
          icon: '📅',
          title: 'Reserva automática',
          description: 'La clienta paga la seña y el horario de 4-6 horas queda bloqueado en tu calendario.',
        },
        {
          icon: '📝',
          title: 'Prueba de mecha',
          description: 'Incluye términos sobre la necesidad de prueba de mecha y salud del cabello antes del procedimiento.',
        },
        {
          icon: '📦',
          title: 'Servicios complejos',
          description: 'Ideal para rubios, coloraciones, alisados y colocación de extensiones.',
        },
      ],
      packages: [
        { name: 'Mechas Creativas', description: 'Técnica personalizada + tratamiento' },
        { name: 'Alisado', description: 'Keratina o botox capilar' },
        { name: 'Extensiones', description: 'Aplicación o mantenimiento de cinta' },
      ],
      testimonial: {
        quote: 'Perdía mucho dinero cuando una clienta de mechas faltaba. Con la seña, ya no faltan.',
        author: 'Jéssica Hair',
        role: 'Estilista',
      },
      faq: [
        {
          question: '¿El valor de la seña se descuenta al final?',
          answer: 'Sí, la seña sirve como reserva. En el día, la clienta paga solo el restante del valor.',
        },
        {
          question: '¿Y si el cabello no pasa la prueba?',
          answer: 'Puedes definir en la política que, si no pasa la prueba, el valor puede convertirse en tratamiento.',
        },
      ],
    },
  },

  'nurses': {
    pt: {
      slug: 'nurses',
      icon: '👩‍⚕️',
      color: 'blue',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Enfermeiro prestando cuidados domiciliares',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Atendimento de home care' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Cuidados de enfermagem' },
        ],
      },
      hero: {
        badge: 'Para Enfermeiros Particulares',
        title: 'Sinal para plantões e procedimentos',
        subtitle: 'Organize sua agenda de home care. Receba antecipado por plantões, curativos e administração de medicamentos.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Segurança financeira',
          description: 'Garanta o pagamento do plantão antes de se deslocar. Evite inadimplência.',
        },
        {
          icon: '📅',
          title: 'Escala organizada',
          description: 'Seus plantões confirmados vão direto para o calendário. Visualize sua semana facilmente.',
        },
        {
          icon: '📝',
          title: 'Dados do paciente',
          description: 'Colete endereço, histórico breve e necessidades especiais no momento do agendamento.',
        },
        {
          icon: '📦',
          title: 'Serviços diversos',
          description: 'Plantão 12h/24h, curativos complexos, injetáveis e banho no leito.',
        },
      ],
      packages: [
        { name: 'Plantão 12h', description: 'Acompanhamento diurno ou noturno' },
        { name: 'Curativo Especial', description: 'Material incluso + visita' },
        { name: 'Aplicação de Medicação', description: 'Visita rápida para administração' },
      ],
      testimonial: {
        quote: 'Me dá muito mais segurança ir para um plantão sabendo que já está pago ou sinalizado.',
        author: 'Enf. Cláudia',
        role: 'Enfermeira Home Care',
        avatar: 'https://randomuser.me/api/portraits/women/7.jpg',
      },
      faq: [
        {
          question: 'Serve para cuidadores também?',
          answer: 'Sim, cuidadores de idosos podem usar para agendar e receber por seus plantões.',
        },
        {
          question: 'Posso cobrar taxa de deslocamento?',
          answer: 'Sim, você pode incluir o valor do deslocamento no preço do serviço ou como um adicional.',
        },
      ],
    },
    en: {
      slug: 'nurses',
      icon: '👩‍⚕️',
      color: 'blue',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Nurse providing home care',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Home care service' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Nursing care' },
        ],
      },
      hero: {
        badge: 'For Private Nurses',
        title: 'Deposit for shifts and procedures',
        subtitle: 'Organize your home care schedule. Get paid upfront for shifts, dressings and medication administration.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Financial security',
          description: 'Ensure shift payment before traveling. Avoid non-payment.',
        },
        {
          icon: '📅',
          title: 'Organized roster',
          description: 'Your confirmed shifts go straight to the calendar. Visualize your week easily.',
        },
        {
          icon: '📝',
          title: 'Patient data',
          description: 'Collect address, brief history and special needs at the time of booking.',
        },
        {
          icon: '📦',
          title: 'Diverse services',
          description: '12h/24h shifts, complex dressings, injectables and bed baths.',
        },
      ],
      packages: [
        { name: '12h Shift', description: 'Day or night monitoring' },
        { name: 'Special Dressing', description: 'Materials included + visit' },
        { name: 'Medication Admin', description: 'Quick visit for administration' },
      ],
      testimonial: {
        quote: 'It gives me much more security going to a shift knowing it is already paid or deposited.',
        author: 'Nurse Claudia',
        role: 'Home Care Nurse',
        avatar: 'https://randomuser.me/api/portraits/women/15.jpg',
      },
      faq: [
        {
          question: 'Does it work for caregivers too?',
          answer: 'Yes, elderly caregivers can use it to schedule and get paid for their shifts.',
        },
        {
          question: 'Can I charge travel fees?',
          answer: 'Yes, you can include the travel cost in the service price or as an add-on.',
        },
      ],
    },
    es: {
      slug: 'nurses',
      icon: '👩‍⚕️',
      color: 'blue',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Enfermero prestando cuidados domiciliarios',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Servicio de home care' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Cuidados de enfermería' },
        ],
      },
      hero: {
        badge: 'Para Enfermeros Particulares',
        title: 'Seña para guardias y procedimientos',
        subtitle: 'Organiza tu agenda de home care. Cobra por adelantado por guardias, curaciones y administración de medicamentos.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Seguridad financiera',
          description: 'Asegura el pago de la guardia antes de desplazarte. Evita morosidad.',
        },
        {
          icon: '📅',
          title: 'Escala organizada',
          description: 'Tus guardias confirmadas van directo al calendario. Visualiza tu semana fácilmente.',
        },
        {
          icon: '📝',
          title: 'Datos del paciente',
          description: 'Recolecta dirección, historia breve y necesidades especiales al momento de agendar.',
        },
        {
          icon: '📦',
          title: 'Servicios diversos',
          description: 'Guardia 12h/24h, curaciones complejas, inyectables y baño en cama.',
        },
      ],
      packages: [
        { name: 'Guardia 12h', description: 'Acompañamiento diurno o nocturno' },
        { name: 'Curación Especial', description: 'Material incluido + visita' },
        { name: 'Aplicación de Medicación', description: 'Visita rápida para administración' },
      ],
      testimonial: {
        quote: 'Me da mucha más seguridad ir a una guardia sabiendo que ya está pagada o señada.',
        author: 'Enf. Claudia',
        role: 'Enfermera Home Care',
      },
      faq: [
        {
          question: '¿Sirve para cuidadores también?',
          answer: 'Sí, cuidadores de ancianos pueden usarlo para agendar y cobrar por sus guardias.',
        },
        {
          question: '¿Puedo cobrar viáticos?',
          answer: 'Sí, puedes incluir el valor del traslado en el precio del servicio o como un adicional.',
        },
      ],
    },
  },

  'pop-up-decorators': {
    pt: {
      slug: 'pop-up-decorators',
      icon: '🎈',
      color: 'yellow',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767705815/Gemini_Generated_Image_st7odxst7odxst7o_tyb0zj.png',
          alt: 'Decorador de festas montando decoração',
        },
        gallery: [
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767705812/Gemini_Generated_Image_st7odxst7odxst7o_1_kdg8fg.png', alt: 'Decoração de festa infantil' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767705802/Gemini_Generated_Image_st7odxst7odxst7o_2_wrfzfx.png', alt: 'Mini table decorada' },
        ],
      },
      hero: {
        badge: 'Para Decoradores de Festas',
        title: 'Sinal para pegue-monte e mini tables',
        subtitle: 'Alugue kits de decoração e mini tables com segurança. Contrato de locação e caução integrados.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Reserva do kit',
          description: 'Cliente paga o sinal e garante a reserva do tema para a data da festa.',
        },
        {
          icon: '📅',
          title: 'Controle de estoque',
          description: 'Evite alugar o mesmo kit para duas pessoas no mesmo dia. Agenda bloqueada automaticamente.',
        },
        {
          icon: '📝',
          title: 'Contrato de locação',
          description: 'Termos sobre cuidado com as peças, devolução e reposição em caso de quebra.',
        },
        {
          icon: '📦',
          title: 'Temas variados',
          description: 'Cadastre seus kits: Bolofofos, Patrulha Canina, Tardezinha, etc.',
        },
      ],
      packages: [
        { name: 'Kit Pegue-Monte', description: 'Cliente retira e monta' },
        { name: 'Mini Table', description: 'Montagem inclusa (pequena)' },
        { name: 'Decoração Completa', description: 'Painel, arco de bolas e mesas' },
      ],
      testimonial: {
        quote: 'Otimizou muito meu pegue-monte. A cliente escolhe o tema, paga e assina o contrato digitalmente.',
        author: 'Patrícia Festas',
        role: 'Decoradora',
        avatar: 'https://randomuser.me/api/portraits/women/8.jpg',
      },
      faq: [
        {
          question: 'Posso cobrar caução/cheque caução?',
          answer: 'Você pode incluir uma taxa de caução no pagamento ou definir em contrato que será cobrado na retirada.',
        },
        {
          question: 'Funciona para retirada e devolução?',
          answer: 'Sim, você define os horários de retirada e devolução no agendamento.',
        },
      ],
    },
    en: {
      slug: 'pop-up-decorators',
      icon: '🎈',
      color: 'yellow',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Pop-up decorator setting up decoration',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Kids party decoration' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Decorated mini table' },
        ],
      },
      hero: {
        badge: 'For Party Decorators',
        title: 'Deposit for grab-and-go and mini tables',
        subtitle: 'Rent decor kits and mini tables securely. Rental contract and security deposit integrated.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Kit reservation',
          description: 'Client pays the deposit and secures the theme reservation for the party date.',
        },
        {
          icon: '📅',
          title: 'Inventory control',
          description: 'Avoid renting the same kit to two people on the same day. Schedule blocked automatically.',
        },
        {
          icon: '📝',
          title: 'Rental contract',
          description: 'Terms about piece care, return and replacement in case of breakage.',
        },
        {
          icon: '📦',
          title: 'Varied themes',
          description: 'Register your kits: Safari, Princess, Superheroes, etc.',
        },
      ],
      packages: [
        { name: 'Grab-and-Go Kit', description: 'Client picks up and sets up' },
        { name: 'Mini Table', description: 'Setup included (small)' },
        { name: 'Full Decor', description: 'Backdrop, balloon arch and tables' },
      ],
      testimonial: {
        quote: 'It optimized my grab-and-go business a lot. The client chooses the theme, pays and signs the contract digitally.',
        author: 'Patricia Parties',
        role: 'Decorator',
        avatar: 'https://randomuser.me/api/portraits/women/16.jpg',
      },
      faq: [
        {
          question: 'Can I charge a security deposit?',
          answer: 'You can include a security deposit fee in the payment or define in contract that it will be charged upon pickup.',
        },
        {
          question: 'Does it work for pickup and return?',
          answer: 'Yes, you define pickup and return times in the scheduling.',
        },
      ],
    },
    es: {
      slug: 'pop-up-decorators',
      icon: '🎈',
      color: 'yellow',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Decorador de fiestas montando decoración',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Decoración de fiesta infantil' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Mini mesa decorada' },
        ],
      },
      hero: {
        badge: 'Para Decoradores de Fiestas',
        title: 'Seña para kits y mini mesas',
        subtitle: 'Alquila kits de decoración y mini mesas con seguridad. Contrato de alquiler y depósito integrados.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Reserva del kit',
          description: 'El cliente paga la seña y asegura la reserva del tema para la fecha de la fiesta.',
        },
        {
          icon: '📅',
          title: 'Control de stock',
          description: 'Evita alquilar el mismo kit a dos personas el mismo día. Agenda bloqueada automáticamente.',
        },
        {
          icon: '📝',
          title: 'Contrato de alquiler',
          description: 'Términos sobre cuidado de las piezas, devolución y reposición en caso de rotura.',
        },
        {
          icon: '📦',
          title: 'Temas variados',
          description: 'Registra tus kits: Safari, Princesas, Superhéroes, etc.',
        },
      ],
      packages: [
        { name: 'Kit Para Llevar', description: 'Cliente retira y monta' },
        { name: 'Mini Mesa', description: 'Montaje incluido (pequeña)' },
        { name: 'Decoración Completa', description: 'Panel, arco de globos y mesas' },
      ],
      testimonial: {
        quote: 'Optimizó mucho mi negocio de alquiler. La clienta elige el tema, paga y firma el contrato digitalmente.',
        author: 'Patricia Fiestas',
        role: 'Decoradora',
      },
      faq: [
        {
          question: '¿Puedo cobrar depósito de garantía?',
          answer: 'Puedes incluir una tasa de garantía en el pago o definir en contrato que se cobrará al retirar.',
        },
        {
          question: '¿Funciona para retiro y devolución?',
          answer: 'Sí, defines los horarios de retiro y devolución en el agendamiento.',
        },
      ],
    },
  },

  'telemedicine-professionals': {
    pt: {
      slug: 'telemedicine-professionals',
      icon: '🩺',
      color: 'cyan',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Profissional de saúde em consulta de telemedicina',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Consulta online por videochamada' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Atendimento médico remoto' },
        ],
      },
      hero: {
        badge: 'Para Profissionais de Saúde',
        title: 'Sinal para consultas online e presenciais',
        subtitle: 'Médicos, psicólogos e terapeutas. Receba pela consulta antes do atendimento. Reduza faltas e organize a agenda.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Consulta paga',
          description: 'O paciente paga para agendar. Fim da inadimplência em consultas particulares.',
        },
        {
          icon: '📅',
          title: 'Agenda médica',
          description: 'Sincronize com seu Google Calendar. Evite conflitos de horário entre clínicas.',
        },
        {
          icon: '📝',
          title: 'Termo de consentimento',
          description: 'Consentimento para telemedicina e política de privacidade de dados (LGPD).',
        },
        {
          icon: '📦',
          title: 'Tipos de atendimento',
          description: 'Primeira consulta, retorno, terapia continuada ou emissão de laudo.',
        },
      ],
      packages: [
        { name: 'Consulta Online', description: 'Videochamada de 50min' },
        { name: 'Consulta Presencial', description: 'Atendimento no consultório' },
        { name: 'Sessão de Terapia', description: 'Acompanhamento semanal' },
      ],
      testimonial: {
        quote: 'Organizou meu consultório particular. O paciente já chega com a consulta paga e o horário reservado.',
        author: 'Dr. André Silva',
        role: 'Psiquiatra',
      },
      faq: [
        {
          question: 'É seguro para dados de saúde?',
          answer: 'Sim, a plataforma foca no agendamento e pagamento. O prontuário você mantém no seu sistema habitual.',
        },
        {
          question: 'Como funciona o retorno?',
          answer: 'Você pode criar um tipo de agendamento "Retorno" com valor zero ou reduzido, se desejar.',
        },
      ],
    },
    en: {
      slug: 'telemedicine-professionals',
      icon: '🩺',
      color: 'cyan',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Healthcare professional in telemedicine consultation',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Online video consultation' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Remote medical care' },
        ],
      },
      hero: {
        badge: 'For Health Professionals',
        title: 'Deposit for online and in-person visits',
        subtitle: 'Doctors, psychologists and therapists. Get paid for the visit before the appointment. Reduce no-shows and organize schedule.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Paid visit',
          description: 'Patient pays to book. End of non-payment in private practice.',
        },
        {
          icon: '📅',
          title: 'Medical schedule',
          description: 'Sync with your Google Calendar. Avoid schedule conflicts between clinics.',
        },
        {
          icon: '📝',
          title: 'Consent form',
          description: 'Consent for telemedicine and data privacy policy (HIPAA/GDPR).',
        },
        {
          icon: '📦',
          title: 'Appointment types',
          description: 'First visit, follow-up, ongoing therapy or report issuance.',
        },
      ],
      packages: [
        { name: 'Online Visit', description: '50min video call' },
        { name: 'In-Person Visit', description: 'Office appointment' },
        { name: 'Therapy Session', description: 'Weekly follow-up' },
      ],
      testimonial: {
        quote: 'It organized my private practice. The patient arrives with the visit already paid and the slot booked.',
        author: 'Dr. Andrew Silva',
        role: 'Psychiatrist',
        avatar: 'https://randomuser.me/api/portraits/men/7.jpg',
      },
      faq: [
        {
          question: 'Is it secure for health data?',
          answer: 'Yes, the platform focuses on scheduling and payment. You keep medical records in your usual system.',
        },
        {
          question: 'How do follow-ups work?',
          answer: 'You can create a "Follow-up" appointment type with zero or reduced cost, if you wish.',
        },
      ],
    },
    es: {
      slug: 'telemedicine-professionals',
      icon: '🩺',
      color: 'cyan',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Profesional de salud en consulta de telemedicina',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Consulta online por videollamada' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Atención médica remota' },
        ],
      },
      hero: {
        badge: 'Para Profesionales de Salud',
        title: 'Seña para consultas online y presenciales',
        subtitle: 'Médicos, psicólogos y terapeutas. Cobra la consulta antes de la atención. Reduce faltas y organiza la agenda.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Consulta pagada',
          description: 'El paciente paga para agendar. Fin de la morosidad en consultas particulares.',
        },
        {
          icon: '📅',
          title: 'Agenda médica',
          description: 'Sincroniza con tu Google Calendar. Evita conflictos de horario entre clínicas.',
        },
        {
          icon: '📝',
          title: 'Consentimiento informado',
          description: 'Consentimiento para telemedicina y política de privacidad de datos.',
        },
        {
          icon: '📦',
          title: 'Tipos de atención',
          description: 'Primera consulta, control, terapia continua o emisión de informe.',
        },
      ],
      packages: [
        { name: 'Consulta Online', description: 'Videollamada de 50min' },
        { name: 'Consulta Presencial', description: 'Atención en consultorio' },
        { name: 'Sesión de Terapia', description: 'Seguimiento semanal' },
      ],
      testimonial: {
        quote: 'Organizó mi consultorio particular. El paciente ya llega con la consulta pagada y el horario reservado.',
        author: 'Dr. Andrés Silva',
        role: 'Psiquiatra',
      },
      faq: [
        {
          question: '¿Es seguro para datos de salud?',
          answer: 'Sí, la plataforma se enfoca en agendamiento y pago. La historia clínica la mantienes en tu sistema habitual.',
        },
        {
          question: '¿Cómo funcionan los controles?',
          answer: 'Puedes crear un tipo de agendamiento "Control" con valor cero o reducido, si deseas.',
        },
      ],
    },
  },
  'tarot-readers': {
    pt: {
      slug: 'tarot-readers',
      icon: '🔮',
      color: 'purple',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699313/Gemini_Generated_Image_pngqmrpngqmrpngq_2_qx3grn.png',
          alt: 'Oraculista fazendo uma leitura',
        },
        gallery: [ 
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699332/Gemini_Generated_Image_pngqmrpngqmrpngq_1_vghvom.png', alt: 'Leitura de tarot online' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699287/Gemini_Generated_Image_84ehhy84ehhy84eh_hwdmnz.png', alt: 'Cartas de tarot dispostas' },
        ],
      },
      hero: {
        badge: 'Para Oraculistas',
        title: 'Sinal para consultas e leituras',
        subtitle: 'Receba antecipado por suas consultas de tarot, numerologia ou astrologia. Organize sua agenda e evite faltas.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Consulta paga',
          description: 'O cliente paga para agendar. Fim da inadimplência em consultas particulares.',
        },
        {
          icon: '📅',
          title: 'Agenda organizada',
          description: 'Sincronize com seu Google Calendar. Evite conflitos de horário.',
        },
        {
          icon: '📝',
          title: 'Termos de serviço',
          description: 'Inclua termos sobre políticas de cancelamento e reembolso.',
        },
        {
          icon: '📦',
          title: 'Tipos de leitura',
          description: 'Tarot, numerologia, astrologia ou outras práticas oraculares.',
        },
      ],
      packages: [
        { name: 'Leitura de Tarot', description: 'Consulta de 30min' },
        { name: 'Mapa Numerológico', description: 'Análise personalizada' },
        { name: 'Consulta Astrológica', description: 'Mapa astral completo' },
      ],
      testimonial: {
        quote: 'Agora meus clientes agendam e pagam antes da consulta. Minha agenda está mais organizada e sem faltas.',  
        author: 'Maria Oliveira',
        role: 'Oraculista',
        avatar: 'https://randomuser.me/api/portraits/women/22.jpg',
      },
      faq: [
        {
          question: 'É seguro para dados pessoais?',
          answer: 'Sim, a plataforma foca em agendamento e pagamento. Seus dados pessoais permanecem protegidos.',
        },
        {
          question: 'Como funcionam os tipos de leitura?',
          answer: 'Você pode criar diferentes tipos de agendamento para cada prática oracular que oferece.',
        },
      ],
    },
    en: {
      slug: 'tarot-readers',
      icon: '🔮',
      color: 'purple',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699313/Gemini_Generated_Image_pngqmrpngqmrpngq_2_qx3grn.png',
          alt: 'Tarot reader doing a reading',
        },
        gallery: [ 
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699332/Gemini_Generated_Image_pngqmrpngqmrpngq_1_vghvom.png', alt: 'Online tarot reading' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699287/Gemini_Generated_Image_84ehhy84ehhy84eh_hwdmnz.png', alt: 'Tarot cards laid out' },
        ],
      },
      hero: {
        badge: 'For Tarot Readers',
        title: 'Deposit for consultations and readings',
        subtitle: 'Get paid upfront for your tarot, numerology or astrology consultations. Organize your schedule and avoid no-shows.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Paid consultation',
          description: 'Client pays to book. End of non-payment in private practice.',
        },
        {
          icon: '📅',
          title: 'Organized schedule',
          description: 'Sync with your Google Calendar. Avoid schedule conflicts.',
        },
        {
          icon: '📝',
          title: 'Terms of service',
          description: 'Include terms about cancellation and refund policies.',
        },
        {
          icon: '📦',
          title: 'Types of readings',
          description: 'Tarot, numerology, astrology or other oracle practices.',
        },
      ],
      packages: [
        { name: 'Tarot Reading', description: '30min consultation' },
        { name: 'Numerology Chart', description: 'Personalized analysis' },
        { name: 'Astrological Consultation', description: 'Complete birth chart' },
      ],
      testimonial: {
        quote: 'Now my clients book and pay before the consultation. My schedule is more organized and no-shows are gone.',  
        author: 'Maria Oliveira',
        role: 'Tarot Reader',
        avatar: 'https://randomuser.me/api/portraits/women/17.jpg',
      },
      faq: [
        {
          question: 'Is it safe for personal data?',
          answer: 'Yes, the platform focuses on booking and payment. Your personal data remains protected.',
        },
        {
          question: 'How do the types of readings work?',
          answer: 'You can create different booking types for each oracle practice you offer.',
        },
      ],
    },
    es: {
      slug: 'tarot-readers',
      icon: '🔮',
      color: 'purple',
      images: {
        hero: {
          src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699313/Gemini_Generated_Image_pngqmrpngqmrpngq_2_qx3grn.png',
          alt: 'Oraculista haciendo una lectura',
        },
        gallery: [ 
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699332/Gemini_Generated_Image_pngqmrpngqmrpngq_1_vghvom.png', alt: 'Lectura de tarot online' },
          { src: 'https://res.cloudinary.com/dyezg9nay/image/upload/v1767699287/Gemini_Generated_Image_84ehhy84ehhy84eh_hwdmnz.png', alt: 'Cartas de tarot dispuestas' },
        ],
      },
      hero: {
        badge: 'Para Oraculistas',
        title: 'Seña para consultas y lecturas',
        subtitle: 'Cobra por adelantado por tus consultas de tarot, numerología o astrología. Organiza tu agenda y evita faltas.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Consulta pagada',
          description: 'El cliente paga para agendar. Fin de la morosidad en consultas particulares.',
        },
        {
          icon: '📅',
          title: 'Agenda organizada',
          description: 'Sincroniza con tu Google Calendar. Evita conflictos de horario.',
        },
        {
          icon: '📝',
          title: 'Términos de servicio',
          description: 'Incluye términos sobre políticas de cancelación y reembolso.',
        },
        {
          icon: '📦',
          title: 'Tipos de lecturas',
          description: 'Tarot, numerología, astrología u otras prácticas oraculares.',
        },
      ],
      packages: [
        { name: 'Lectura de Tarot', description: 'Consulta de 30 minutos' },
        { name: 'Carta Numerológica', description: 'Análisis personalizado' },
        { name: 'Consulta Astrológica', description: 'Carta natal completa' },
      ],
      testimonial: {
        quote: 'Ahora mis clientes reservan y pagan antes de la consulta. Mi agenda está más organizada y las faltas desaparecieron.',  
        author: 'Maria Oliveira',
        role: 'Oraculista',
        avatar: 'https://randomuser.me/api/portraits/women/27.jpg',
      },
      faq: [
        {
          question: '¿Es seguro para datos personales?',
          answer: 'Sí, la plataforma se enfoca en reservas y pagos. Tus datos personales permanecen protegidos.',
        },
        {
          question: '¿Cómo funcionan los tipos de lecturas?',
          answer: 'Puedes crear diferentes tipos de reserva para cada práctica oracular que ofreces.',
        },
      ],
    },
  },

  'reiki-practitioners': {
    pt: {
      slug: 'reiki-practitioners',
      icon: '✨',
      color: 'indigo',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Terapeuta de Reiki em sessão',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Sessão de Reiki' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Ambiente terapêutico' },
        ],
      },
      hero: {
        badge: 'Para Terapeutas de Reiki',
        title: 'Sinal para sessões de energia',
        subtitle: 'Receba antecipadamente por suas sessões de Reiki, cura energética ou terapias holísticas. Organize sua agenda e evite faltas.',
        cta: 'Criar conta gratuitamente',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Sessão garantida',
          description: 'O cliente paga para agendar. Fim das faltas em sessões particulares.',
        },
        {
          icon: '📅',
          title: 'Agenda organizada',
          description: 'Sincronize com seu Google Calendar. Evite conflitos de horário.',
        },
        {
          icon: '📝',
          title: 'Termos de atendimento',
          description: 'Inclua termos sobre preparação, contraindicações e política de cancelamento.',
        },
        {
          icon: '📦',
          title: 'Tipos de sessões',
          description: 'Reiki presencial, à distância, pacotes de sessões ou formações.',
        },
      ],
      packages: [
        { name: 'Sessão Individual', description: 'Sessão de 1 hora de Reiki' },
        { name: 'Pacote 4 Sessões', description: 'Tratamento completo com desconto' },
        { name: 'Reiki à Distância', description: 'Sessão online de energia' },
      ],
      testimonial: {
        quote: 'Agora meus clientes agendam e pagam antes da sessão. Minha agenda está mais organizada e as faltas sumiram.',
        author: 'Carla Luz',
        role: 'Mestre Reiki',
        avatar: 'https://randomuser.me/api/portraits/women/23.jpg',
      },
      faq: [
        {
          question: 'Como funciona para sessões à distância?',
          answer: 'Você pode criar tipos de sessão diferentes para presencial e à distância, cada um com seu preço.',
        },
        {
          question: 'Posso vender pacotes de sessões?',
          answer: 'Sim! Crie pacotes com múltiplas sessões e ofereça descontos para tratamentos completos.',
        },
      ],
    },
    en: {
      slug: 'reiki-practitioners',
      icon: '✨',
      color: 'indigo',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Reiki therapist in session',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Reiki session' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Therapeutic environment' },
        ],
      },
      hero: {
        badge: 'For Reiki Practitioners',
        title: 'Deposit for energy sessions',
        subtitle: 'Get paid in advance for your Reiki, energy healing or holistic therapy sessions. Organize your schedule and avoid no-shows.',
        cta: 'Create account for free',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Guaranteed session',
          description: 'Client pays to book. No more no-shows for private sessions.',
        },
        {
          icon: '📅',
          title: 'Organized schedule',
          description: 'Sync with your Google Calendar. Avoid scheduling conflicts.',
        },
        {
          icon: '📝',
          title: 'Service terms',
          description: 'Include terms about preparation, contraindications and cancellation policy.',
        },
        {
          icon: '📦',
          title: 'Session types',
          description: 'In-person Reiki, distance healing, session packages or training.',
        },
      ],
      packages: [
        { name: 'Individual Session', description: '1-hour Reiki session' },
        { name: '4 Session Package', description: 'Complete treatment with discount' },
        { name: 'Distance Reiki', description: 'Online energy session' },
      ],
      testimonial: {
        quote: 'Now my clients book and pay before the session. My schedule is more organized and no-shows disappeared.',
        author: 'Emily Light',
        role: 'Reiki Master',
        avatar: 'https://randomuser.me/api/portraits/women/18.jpg',
      },
      faq: [
        {
          question: 'How does it work for distance sessions?',
          answer: 'You can create different session types for in-person and distance, each with its own price.',
        },
        {
          question: 'Can I sell session packages?',
          answer: 'Yes! Create packages with multiple sessions and offer discounts for complete treatments.',
        },
      ],
    },
    es: {
      slug: 'reiki-practitioners',
      icon: '✨',
      color: 'indigo',
      images: {
        hero: {
          src: '/images/solutions/placeholder-hero.svg',
          alt: 'Terapeuta de Reiki en sesión',
        },
        gallery: [
          { src: '/images/solutions/placeholder-1.svg', alt: 'Sesión de Reiki' },
          { src: '/images/solutions/placeholder-2.svg', alt: 'Ambiente terapéutico' },
        ],
      },
      hero: {
        badge: 'Para Terapeutas de Reiki',
        title: 'Seña para sesiones de energía',
        subtitle: 'Cobra por adelantado por tus sesiones de Reiki, sanación energética o terapias holísticas. Organiza tu agenda y evita faltas.',
        cta: 'Crear cuenta gratis',
      },
      benefits: [
        {
          icon: '💰',
          title: 'Sesión garantizada',
          description: 'El cliente paga para agendar. Fin de las faltas en sesiones particulares.',
        },
        {
          icon: '📅',
          title: 'Agenda organizada',
          description: 'Sincroniza con tu Google Calendar. Evita conflictos de horario.',
        },
        {
          icon: '📝',
          title: 'Términos de atención',
          description: 'Incluye términos sobre preparación, contraindicaciones y política de cancelación.',
        },
        {
          icon: '📦',
          title: 'Tipos de sesiones',
          description: 'Reiki presencial, a distancia, paquetes de sesiones o formaciones.',
        },
      ],
      packages: [
        { name: 'Sesión Individual', description: 'Sesión de 1 hora de Reiki' },
        { name: 'Paquete 4 Sesiones', description: 'Tratamiento completo con descuento' },
        { name: 'Reiki a Distancia', description: 'Sesión online de energía' },
      ],
      testimonial: {
        quote: 'Ahora mis clientes agendan y pagan antes de la sesión. Mi agenda está más organizada y las faltas desaparecieron.',
        author: 'María Luz',
        role: 'Maestra Reiki',
        avatar: 'https://randomuser.me/api/portraits/women/28.jpg',
      },
      faq: [
        {
          question: '¿Cómo funciona para sesiones a distancia?',
          answer: 'Puedes crear tipos de sesión diferentes para presencial y a distancia, cada uno con su precio.',
        },
        {
          question: '¿Puedo vender paquetes de sesiones?',
          answer: '¡Sí! Crea paquetes con múltiples sesiones y ofrece descuentos para tratamientos completos.',
        },
      ],
    },
  },
}

export function getSolutionContent(slug: SolutionSlug, language: SupportedLanguage): SolutionContent {
  return SOLUTIONS_CONTENT[slug][language]
}

export function getAllSolutionSlugs(): SolutionSlug[] {
  return Object.keys(SOLUTIONS_CONTENT) as SolutionSlug[]
}
